import React, { useState } from "react";
import { getToken } from "../lib/auth";

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current" strokeWidth="1.5">
      <rect x="9" y="2" width="6" height="11" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0M12 19v3M9 22h6" />
    </svg>
  );
}

export default function VoiceCallButton({ targetUserId, className = "" }) {
  const [state, setState] = useState("idle"); // idle | connecting | in_call | ended
  const [sessionId, setSessionId] = useState(null);
  const [permError, setPermError] = useState("");

  const startCall = async () => {
    setPermError("");
    setState("connecting");

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setState("idle");
      setPermError("Microphone permission required.");
      return;
    }

    try {
      const token = getToken();
      const res = await fetch("/api/voice/session/start", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ target_user_id: targetUserId || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      setSessionId(data.session_id);
      setState("in_call");
    } catch {
      setState("idle");
    }
  };

  const endCall = () => {
    if (sessionId) {
      const token = getToken();
      fetch("/api/voice/session/end", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ session_id: sessionId }),
      }).catch(() => {});
    }
    setSessionId(null);
    setState("ended");
  };

  return (
    <div className={className}>
      {state === "idle" && (
        <button
          data-testid="voice-call-start"
          onClick={startCall}
          className="flex items-center gap-2 rounded-full border border-white/15 hover:border-[rgba(212,175,55,0.4)] text-white/85 px-5 py-3 text-sm transition-colors"
        >
          <MicIcon />
          Voice chat
        </button>
      )}

      {state === "connecting" && (
        <button
          data-testid="voice-call-connecting"
          disabled
          className="flex items-center gap-2 rounded-full border border-white/20 text-white/50 px-5 py-3 text-sm cursor-wait"
        >
          <span className="h-2 w-2 rounded-full bg-[#D4AF37] animate-pulse" />
          Connecting…
        </button>
      )}

      {state === "in_call" && (
        <button
          data-testid="voice-call-end"
          onClick={endCall}
          className="flex items-center gap-2 rounded-full bg-[#B93A50] text-white font-semibold px-5 py-3 text-sm hover:bg-[#a0303e] transition-colors"
        >
          <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
          End call
        </button>
      )}

      {state === "ended" && (
        <button
          data-testid="voice-call-ended"
          onClick={() => setState("idle")}
          className="flex items-center gap-2 rounded-full border border-white/10 text-white/40 px-5 py-3 text-sm hover:border-white/20 hover:text-white/60 transition-colors"
        >
          <MicIcon />
          Call ended · retry
        </button>
      )}

      {permError && (
        <p className="mt-1.5 text-red-400 text-xs" data-testid="voice-perm-error">{permError}</p>
      )}
    </div>
  );
}
